/**
 * Adding and editing an expense.
 *
 * This was an inline form that unfolded above the list and pushed every row
 * down by its own height, which is the pre-redesign habit: the operator lost
 * sight of the rows they were comparing the new expense against. It is a dialog
 * now, the same shape as the client form.
 */
import { useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { createExpense, updateExpense, type Expense } from "../../../lib/sales";
import { Btn, Modal, useToast } from "../../ui";
import { ClientPicker, ProjectPicker, centsToInput, inputToCents } from "../sales/shared";

/** A label over a field, the way the design writes forms. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <label className="crm-lab">{label}</label>
      {children}
    </>
  );
}

/** One sunken pill holding a plain input. */
function Pill({
  value,
  onChange,
  placeholder,
  type,
  inputMode,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: "numeric" | "decimal" | "text";
  required?: boolean;
}) {
  return (
    <label className="crm-inp">
      <input
        type={type ?? "text"}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
      />
    </label>
  );
}

/**
 * An ISO timestamp as the `yyyy-mm-dd` a date input wants, in local time.
 * Reading the date out of the ISO string directly would show yesterday for
 * anybody east of UTC whenever the expense was booked late in the evening.
 */
function toDateInput(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function ExpenseModal({
  expense,
  preset,
  onClose,
  onSaved,
}: {
  /** null for a new expense, the record for an edit. */
  expense: Expense | null;
  /**
   * What a new expense is booked to when the form is opened from the record it
   * belongs to rather than from the expenses list.
   *
   * Ignored on an edit: the expense already says where it belongs, and a preset
   * that overrode it would silently move somebody's cost the moment they opened
   * the form to fix a typo.
   */
  preset?: { clientId: string; projectId: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();

  const [category, setCategory] = useState(expense?.category ?? "");
  const [vendor, setVendor] = useState(expense?.vendor ?? "");
  const [description, setDescription] = useState(expense?.description ?? "");
  const [amount, setAmount] = useState(centsToInput(expense?.amountCents ?? 0));
  const [tax, setTax] = useState(centsToInput(expense?.taxCents ?? 0));
  const [date, setDate] = useState(toDateInput(expense?.spentAt));
  const [clientId, setClientId] = useState(expense?.clientId ?? preset?.clientId ?? "");
  /**
   * The project this spend is booked against.
   *
   * The field this form was missing, and the reason every project's spend
   * figure was labour time calling itself total spend: the column exists, the
   * route takes it, the project summary adds it up, and no screen ever wrote
   * it. Nothing else on this form is load-bearing for a number somewhere else.
   */
  const [projectId, setProjectId] = useState(expense?.projectId ?? preset?.projectId ?? "");
  const [billable, setBillable] = useState(expense?.billable ?? false);
  const [busy, setBusy] = useState(false);

  /**
   * Both links are statements rather than questions when the form was opened
   * from a project.
   *
   * The pair has to hold: an expense belongs to a project, a project belongs to
   * exactly one client, and the route refuses a pair that disagrees. Changing
   * the client here would clear the project two lines below - which the API
   * would accept, so the expense would save happily and then never appear on the
   * tab the operator opened this form from. That is the silent version of the
   * refusal, and it is the one that actually happens. Locking both fields makes
   * the pre-fill survive the picker instead of asking the operator to notice a
   * row that is not there. Nothing is lost: an expense for another client or
   * another project is one click away on the expenses page, where both fields
   * are open.
   */
  const linkLocked = !expense && !!preset?.projectId;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!category.trim() || busy) return;
    setBusy(true);
    try {
      const body = {
        category: category.trim(),
        vendor: vendor.trim() || undefined,
        description: description.trim() || undefined,
        amountCents: inputToCents(amount),
        taxCents: inputToCents(tax),
        clientId: clientId || undefined,
        // An id or an explicit null, never an empty string: the route takes
        // `z.string().optional().nullable()` and turns a blank into a real NULL,
        // the same contract the invoice and estimate forms write against. Sending
        // null unconditionally is safe on a create too - the column is nullable
        // and "no project" is what a new expense means when nobody chose one.
        projectId: projectId || null,
        billable,
        // Midday local rather than midnight: the server stores an instant, and
        // a midnight stamp in a positive offset lands on the previous day once
        // it is converted to UTC.
        spentAt: date ? new Date(`${date}T12:00:00`).toISOString() : undefined,
      };
      if (expense) await updateExpense(expense.id, body);
      else await createExpense(body);
      onSaved();
      onClose();
    } catch (e) {
      // The project link is the one field whose refusal has a name. The picker
      // below only offers projects of the chosen client, and a preset pair is
      // locked outright, so this should be unreachable from this form - but
      // there is no foreign key behind `projectId`, the route checks the pair
      // itself, and a refusal it can actually send must not arrive as
      // "something went wrong".
      const mismatch = e instanceof ApiError && (e.body as { error?: string } | null)?.error === "project_client_mismatch";
      toast(t(mismatch ? "sales.projectMismatch" : "common.error"));
    } finally {
      setBusy(false);
    }
  }

  const title = expense ? t("expenses.editTitle") : t("expenses.addTitle");

  return (
    <Modal title={title} label={title} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label={t("expenses.category")}>
          <Pill
            value={category}
            onChange={setCategory}
            placeholder={t("expenses.categoryPlaceholder")}
            required
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Field label={t("expenses.vendor")}>
              <Pill value={vendor} onChange={setVendor} />
            </Field>
          </div>
          <div>
            <Field label={t("expenses.date")}>
              {/* The shared calendar, like every other date in the CRM: the native
                  input is a different control in every browser. */}
              <DatePicker value={date} onChange={setDate} locale={locale} placeholder={t("expenses.date")} />
            </Field>
          </div>
          <div>
            <Field label={t("expenses.amount")}>
              <Pill value={amount} onChange={setAmount} inputMode="decimal" />
            </Field>
          </div>
          <div>
            <Field label={t("expenses.tax")}>
              <Pill value={tax} onChange={setTax} inputMode="decimal" />
            </Field>
          </div>
        </div>

        <Field label={t("expenses.client")}>
          <ClientPicker
            value={clientId}
            disabled={linkLocked}
            onChange={(next) => {
              // A project belongs to exactly one client, so changing the client
              // invalidates whatever project was picked under the old one.
              // Cleared here rather than sent and refused: the picker below
              // would otherwise keep showing a name that no longer belongs on
              // this expense, and the spend would land in another client's
              // project total.
              if (next !== clientId) setProjectId("");
              setClientId(next);
            }}
          />
        </Field>

        {/* Scoped to the client above, and disabled with a reason until there is
            one: an expense with no client cannot be checked against a project's
            owner at all. */}
        <Field label={t("expenses.project")}>
          <ProjectPicker
            clientId={clientId}
            value={projectId}
            onChange={setProjectId}
            disabled={linkLocked}
          />
        </Field>

        <label className="crm-inp mt-2.5 cursor-pointer">
          <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
          <span className="text-[13.5px]">{t("expenses.billable")}</span>
        </label>

        <Field label={t("expenses.description")}>
          <textarea
            className="crm-area"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("expenses.descriptionPlaceholder")}
          />
        </Field>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("expenses.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !category.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("expenses.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
